import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteB2BEwayBillDocument,
  deleteB2BOrderDocument,
  deleteB2BShipment,
  getB2BOrder,
  getB2BOrderHistory,
  uploadB2BEwayBillDocument,
  uploadB2BInvoiceDocument,
} from '../../services/shipturtleApi';
import { showToast } from '../../utils/toast';
import { useTaraTerminalFocus } from '../../utils/taraTerminalContext';
import B2BConfirmModal from './B2BConfirmModal';
import B2BDocumentsModal from './B2BDocumentsModal';
import B2BShipmentModal from './B2BShipmentModal';
import { b2bDocumentFileName, openB2BDocument } from './b2bDocuments';
import { b2bOrderStatusClass, b2bStatusClass } from './b2bStatusBadge';
import './B2BOrders.css';

/**
 * One B2B order, as a modal over the list.
 *
 * A modal rather than its own page, matching how manual orders open: the list
 * is where ops works, and losing it to navigate into a read-only view — then
 * navigating back — was the wrong trade for a screen nobody edits in place.
 * Editing still routes to the wizard, so an order is only ever written in one
 * place.
 *
 * Everything shown here is selectable, so a value can be copied out without
 * opening the edit form just to reach an input.
 */

/* The order's two fixed document slots. Each is a column on the order, never
   more than one of either; everything else is an additional document. */
const FIXED_DOCUMENTS = [
  {
    key: 'invoice',
    label: 'Invoice',
    field: 'invoice_document_url',
    upload: uploadB2BInvoiceDocument,
    remove: null,
  },
  {
    key: 'eway',
    label: 'E-way bill',
    field: 'eway_bill_document_url',
    upload: uploadB2BEwayBillDocument,
    // Optional, so it can be taken off again; the invoice is replaced, never
    // removed.
    remove: deleteB2BEwayBillDocument,
  },
];

const shortDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '';

const money = (v) =>
  v === null || v === undefined || v === ''
    ? '—'
    : `₹${Number(v).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/* Renders a structured address the way it is read on a label: company, street,
   then the city/state/pincode line. Empty parts are dropped rather than leaving
   stray commas. */
const addressLines = (order, prefix) =>
  [
    order[`${prefix}_company_name`],
    order[`${prefix}_address_line_1`],
    order[`${prefix}_address_line_2`],
    order[`${prefix}_landmark`],
    [order[`${prefix}_city`], order[`${prefix}_state`], order[`${prefix}_pincode`]]
      .filter(Boolean)
      .join(', '),
    order[`${prefix}_country`],
  ].filter(Boolean);

export default function B2BOrderDetail({
  orderId,
  initialTab = 'details',
  onClose,
  onChanged,
}) {
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(initialTab);
  // Mounted only while the order's modal is open.
  useTaraTerminalFocus('open_b2b_order', {
    order_number: order?.order_number,
    order_id: orderId,
    tab,
  });
  const [shipmentModal, setShipmentModal] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [documentsModal, setDocumentsModal] = useState(false);
  // { kind: 'fixed', slot } or { kind: 'additional', document }.
  const [removeDocTarget, setRemoveDocTarget] = useState(null);
  const [busyDoc, setBusyDoc] = useState('');
  const filePickers = useRef({});
  // Where the last press began. A text selection that starts inside the panel
  // and is released over the backdrop still fires a click on the backdrop, so
  // closing on click alone threw the order away mid-copy.
  const pressedOnBackdrop = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orderData, historyData] = await Promise.all([
        getB2BOrder(orderId),
        getB2BOrderHistory(orderId).catch(() => ({ history: [] })),
      ]);
      setOrder(orderData.order);
      setHistory(historyData.history || []);
    } catch (err) {
      setError(err?.message || 'Could not load this order.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      // Only the top-most layer closes, so Escape in the shipment form does not
      // dismiss the order behind it too.
      if (
        e.key === 'Escape' &&
        !shipmentModal &&
        !removeTarget &&
        !documentsModal &&
        !removeDocTarget
      ) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, shipmentModal, removeTarget, documentsModal, removeDocTarget]);

  const refresh = () => {
    load();
    onChanged?.();
  };

  const handleDeleteShipment = async () => {
    const shipment = removeTarget;
    if (!shipment) return;

    setRemoving(true);
    try {
      await deleteB2BShipment(shipment.id);
      showToast({ type: 'success', message: 'Shipment removed.' });
      setRemoveTarget(null);
      refresh();
    } catch (err) {
      showToast({ type: 'error', message: err?.message || 'Could not remove it.' });
    } finally {
      setRemoving(false);
    }
  };

  const handleFixedUpload = async (slot, file) => {
    if (!file) return;
    setBusyDoc(slot.key);
    try {
      await slot.upload(order.id, file);
      showToast({ type: 'success', message: `${slot.label} uploaded.` });
      refresh();
    } catch (err) {
      showToast({ type: 'error', message: err?.message || 'The upload failed.' });
    } finally {
      setBusyDoc('');
    }
  };

  const handleRemoveDocument = async () => {
    const target = removeDocTarget;
    if (!target) return;

    setRemoving(true);
    try {
      if (target.kind === 'fixed') {
        await target.slot.remove(order.id);
      } else {
        await deleteB2BOrderDocument(target.document.id);
      }
      showToast({ type: 'success', message: 'Document removed.' });
      setRemoveDocTarget(null);
      refresh();
    } catch (err) {
      showToast({ type: 'error', message: err?.message || 'Could not remove it.' });
    } finally {
      setRemoving(false);
    }
  };

  const documentCount = order
    ? FIXED_DOCUMENTS.filter((slot) => order[slot.field]).length +
      (order.documents || []).length
    : 0;

  return (
    <div
      className="b2bModalBackdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedOnBackdrop.current && e.target === e.currentTarget) onClose();
      }}
    >
      <div className="b2bModalPanel" onClick={(e) => e.stopPropagation()}>
        {loading && (
          <div className="b2bModalBody">
            <p className="b2bMuted">Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="b2bModalBody">
            <p className="b2bError">{error}</p>
          </div>
        )}

        {!loading && !error && order && (
          <>
            <header className="b2bModalHead">
              <div className="b2bSelectable">
                <h3>{order.order_number}</h3>
                <span className="b2bModalSub">
                  {order.order_date || 'No date'}
                  {order.mode ? ` · ${order.mode}` : ''}
                </span>
              </div>
              <div className="b2bModalHeadActions">
                <span className={`b2bStatusBadge ${b2bOrderStatusClass(order)}`}>
                  {order.derived_status_label}
                </span>
                <button
                  type="button"
                  className="btn btnGhost b2bSmallBtn"
                  onClick={() =>
                    navigate(`/suspire-terminal/b2b-orders/${order.id}/edit`)
                  }
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="b2bModalClose"
                  onClick={onClose}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </header>

            <nav className="b2bTabs" role="tablist">
              {[
                ['details', 'Details'],
                ['items', `Vendors & products (${(order.vendors || []).length})`],
                ['shipments', `Shipments (${(order.shipments || []).length})`],
                ['documents', `Documents (${documentCount})`],
                ['history', 'History'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={`b2bTab ${tab === key ? 'isActive' : ''}`}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              ))}
            </nav>

            <div className="b2bModalBody b2bSelectable">
              {tab === 'details' && (
                <>
                  <dl className="b2bFacts">
                    <div>
                      <dt>Invoice</dt>
                      <dd>{order.invoice_number || '—'}</dd>
                    </div>
                    <div>
                      <dt>Invoice date</dt>
                      <dd>{order.invoice_date || '—'}</dd>
                    </div>
                    <div>
                      <dt>Invoice value</dt>
                      <dd>
                        {money(order.invoice_value)}
                        {order.invoice_value_overridden && (
                          <span className="b2bOverrideNote">
                            {' '}
                            overridden · computed {money(order.computed_value)}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Total quantity</dt>
                      <dd>
                        {order.total_quantity ?? '—'}
                        {order.total_quantity_overridden && (
                          <span className="b2bOverrideNote">
                            {' '}
                            overridden · computed {order.computed_quantity}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>Boxes</dt>
                      <dd>{(order.boxes || []).length || '—'}</dd>
                    </div>
                    {FIXED_DOCUMENTS.map((slot) => (
                      <div key={slot.key}>
                        <dt>{slot.label}</dt>
                        <dd>
                          {order[slot.field] ? (
                            <a href={order[slot.field]} target="_blank" rel="noreferrer">
                              View
                            </a>
                          ) : (
                            '—'
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  <div className="b2bAddrPair">
                    <section>
                      <h4>Pickup from</h4>
                      <p>
                        {addressLines(order, 'pickup').map((line, i) => (
                          <span className="b2bAddrLine" key={i}>
                            {line}
                          </span>
                        ))}
                      </p>
                      {order.pickup_gstin && (
                        <span className="b2bGstin">GSTIN {order.pickup_gstin}</span>
                      )}
                      {(order.pickup_poc_name || order.pickup_poc_contact) && (
                        <span className="b2bSub">
                          {order.pickup_poc_name}
                          {order.pickup_poc_contact ? ` · ${order.pickup_poc_contact}` : ''}
                          {order.pickup_poc_email ? ` · ${order.pickup_poc_email}` : ''}
                        </span>
                      )}
                    </section>

                    <section>
                      <h4>Deliver to</h4>
                      <p>
                        {addressLines(order, 'delivery').map((line, i) => (
                          <span className="b2bAddrLine" key={i}>
                            {line}
                          </span>
                        ))}
                      </p>
                      {order.delivery_gstin && (
                        <span className="b2bGstin">GSTIN {order.delivery_gstin}</span>
                      )}
                      <span className="b2bSub">
                        {order.customer_name}
                        {order.customer_contact ? ` · ${order.customer_contact}` : ''}
                        {order.customer_email ? ` · ${order.customer_email}` : ''}
                      </span>
                      {order.timeline_commitment && (
                        <span className="b2bSub">
                          Commitment: {order.timeline_commitment}
                        </span>
                      )}
                    </section>
                  </div>

                  {order.notes && (
                    <section className="b2bNotes">
                      <h4>Internal notes</h4>
                      <p>{order.notes}</p>
                    </section>
                  )}
                </>
              )}

              {tab === 'items' && (
                <>
                  {(order.vendors || []).map((vendor) => (
                    <div className="b2bVendorBlock" key={vendor.id}>
                      <div className="b2bVendorBlockHead">
                        <strong>{vendor.vendor_name}</strong>
                        <span>
                          {vendor.total_quantity} units · {money(vendor.total_value)}
                        </span>
                      </div>
                      <table className="b2bMiniTable">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>SKU</th>
                            <th className="b2bNum">Qty</th>
                            <th className="b2bNum">Total amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vendor.products.map((p) => (
                            <tr key={p.id}>
                              <td>{p.product_name}</td>
                              <td>{p.sku || '—'}</td>
                              <td className="b2bNum">{p.quantity}</td>
                              <td className="b2bNum">{money(p.total_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {!(order.vendors || []).length && (
                    <p className="b2bMuted">No vendors recorded.</p>
                  )}

                  {(order.boxes || []).length > 0 && (
                    <div className="b2bVendorBlock">
                      <div className="b2bVendorBlockHead">
                        <strong>Packaging</strong>
                        <span>{order.boxes.length} boxes</span>
                      </div>
                      <table className="b2bMiniTable">
                        <thead>
                          <tr>
                            <th>Box</th>
                            <th>Dimensions</th>
                            <th>Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.boxes.map((b, i) => (
                            <tr key={`${b.name}-${i}`}>
                              <td>{b.name}</td>
                              <td>
                                {b.length && b.breadth && b.height
                                  ? `${b.length} × ${b.breadth} × ${b.height} ${b.unit || ''}`
                                  : '—'}
                              </td>
                              <td>
                                {b.weight ? `${b.weight} ${b.weight_unit || ''}` : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {tab === 'shipments' && (
                <>
                  {(order.shipments || []).length === 0 && (
                    <p className="b2bEmptyStage">
                      No shipment yet. Once the consignment is booked with a courier, add
                      its AWB here.
                    </p>
                  )}

                  {(order.shipments || []).map((shipment) => (
                    <div className="b2bShipmentRow" key={shipment.id}>
                      <div className="b2bShipmentMain">
                        <div>
                          <span className="b2bShipmentLabel">AWB</span>
                          <strong>{shipment.awb_number}</strong>
                        </div>
                        <div>
                          <span className="b2bShipmentLabel">Shipper</span>
                          {shipment.shipper}
                        </div>
                        <div>
                          <span className="b2bShipmentLabel">Status</span>
                          <span
                            className={`b2bStatusBadge ${b2bStatusClass(shipment.progress)}`}
                          >
                            {shipment.progress ? shipment.progress_label : 'No update'}
                          </span>
                        </div>
                        <div>
                          <span className="b2bShipmentLabel">EDD</span>
                          {shipment.expected_delivery_date || '—'}
                        </div>
                        <div>
                          <span className="b2bShipmentLabel">Charges</span>
                          {money(shipment.b2b_shipping_charges)}
                        </div>
                      </div>
                      <div className="b2bShipmentSide">
                        {shipment.tracking_link && (
                          <a href={shipment.tracking_link} target="_blank" rel="noreferrer">
                            Track
                          </a>
                        )}
                        <button
                          type="button"
                          className="b2bLinkBtn"
                          onClick={() => setShipmentModal({ shipment })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="b2bLinkBtn b2bDanger"
                          onClick={() => setRemoveTarget(shipment)}
                        >
                          Remove
                        </button>
                      </div>
                      {shipment.remarks && (
                        <p className="b2bShipmentRemarks">{shipment.remarks}</p>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="btn btnPrimary b2bAddShipment"
                    onClick={() => setShipmentModal({ shipment: null })}
                  >
                    + Add shipment
                  </button>
                </>
              )}

              {tab === 'documents' && (
                <>
                  <h4 className="b2bDocHeading">Order documents</h4>
                  <ul className="b2bDocList">
                    {FIXED_DOCUMENTS.map((slot) => {
                      const url = order[slot.field];
                      const busy = busyDoc === slot.key;
                      return (
                        <li className="b2bDocItem" key={slot.key}>
                          <span className="b2bDocIcon" aria-hidden="true">
                            PDF
                          </span>
                          <div className="b2bDocInfo">
                            <strong>{slot.label}</strong>
                            <span className="b2bSub">
                              {url ? b2bDocumentFileName(url) : 'Not uploaded'}
                            </span>
                          </div>
                          <div className="b2bDocActions">
                            <input
                              ref={(el) => {
                                filePickers.current[slot.key] = el;
                              }}
                              type="file"
                              accept="application/pdf"
                              hidden
                              onChange={(e) => {
                                handleFixedUpload(slot, e.target.files?.[0]);
                                e.target.value = '';
                              }}
                            />
                            {url && (
                              <button
                                type="button"
                                className="btn btnPrimary b2bSmallBtn"
                                onClick={() => openB2BDocument(url)}
                              >
                                Download
                              </button>
                            )}
                            <button
                              type="button"
                              className="b2bLinkBtn"
                              disabled={busy}
                              onClick={() => filePickers.current[slot.key]?.click()}
                            >
                              {busy ? 'Uploading…' : url ? 'Replace' : 'Upload'}
                            </button>
                            {url && slot.remove && (
                              <button
                                type="button"
                                className="b2bLinkBtn b2bDanger"
                                disabled={busy}
                                onClick={() => setRemoveDocTarget({ kind: 'fixed', slot })}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="b2bDocHeadingRow">
                    <h4 className="b2bDocHeading">
                      Additional documents ({(order.documents || []).length})
                    </h4>
                    <button
                      type="button"
                      className="b2bAddLine"
                      onClick={() => setDocumentsModal(true)}
                    >
                      <span className="b2bAddLineIcon">+</span>
                      Add documents
                    </button>
                  </div>

                  {(order.documents || []).length === 0 ? (
                    <p className="b2bEmptyStage">
                      Nothing else attached. Use Add documents for packing lists, POs,
                      challans or anything else this order needs.
                    </p>
                  ) : (
                    <ul className="b2bDocList">
                      {order.documents.map((doc) => (
                        <li className="b2bDocItem" key={doc.id}>
                          <span className="b2bDocIcon" aria-hidden="true">
                            {(
                              (doc.original_filename || b2bDocumentFileName(doc.file_url))
                                .split('.')
                                .pop() || 'FILE'
                            )
                              .slice(0, 4)
                              .toUpperCase()}
                          </span>
                          <div className="b2bDocInfo">
                            <strong>{doc.name}</strong>
                            <span className="b2bSub">
                              {[
                                doc.original_filename || b2bDocumentFileName(doc.file_url),
                                doc.uploaded_by_name,
                                shortDate(doc.created_at),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          </div>
                          <div className="b2bDocActions">
                            <button
                              type="button"
                              className="btn btnPrimary b2bSmallBtn"
                              onClick={() => openB2BDocument(doc.file_url)}
                            >
                              Download
                            </button>
                            <button
                              type="button"
                              className="b2bLinkBtn b2bDanger"
                              onClick={() =>
                                setRemoveDocTarget({ kind: 'additional', document: doc })
                              }
                            >
                              Remove
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {tab === 'history' && (
                <>
                  {history.length === 0 && <p className="b2bMuted">Nothing recorded yet.</p>}
                  <ul className="b2bHistory">
                    {history.map((entry) => (
                      <li key={entry.id}>
                        <span className="b2bHistoryWhen">
                          {new Date(entry.created_at).toLocaleString('en-IN')}
                        </span>
                        <span className="b2bHistoryWhat">
                          <strong>{entry.action_label}</strong>
                          {Object.keys(entry.changes || {}).length > 0 && (
                            <span className="b2bHistoryDiff">
                              {Object.entries(entry.changes)
                                .map(
                                  ([field, [from, to]]) =>
                                    `${field}: ${from ?? '—'} → ${to ?? '—'}`
                                )
                                .join(' · ')}
                            </span>
                          )}
                        </span>
                        <span className="b2bHistoryWho">{entry.changed_by_name}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {removeTarget && (
        <B2BConfirmModal
          title={`Remove shipment ${removeTarget.awb_number}?`}
          body="The order stays; only this consignment is removed."
          confirmLabel="Remove shipment"
          danger
          busy={removing}
          onConfirm={handleDeleteShipment}
          onClose={() => setRemoveTarget(null)}
        />
      )}

      {removeDocTarget && (
        <B2BConfirmModal
          title={`Remove ${
            removeDocTarget.kind === 'fixed'
              ? `the ${removeDocTarget.slot.label.toLowerCase()}`
              : `"${removeDocTarget.document.name}"`
          }?`}
          body="It is detached from this order. The change is recorded in the order's history."
          confirmLabel="Remove document"
          danger
          busy={removing}
          onConfirm={handleRemoveDocument}
          onClose={() => setRemoveDocTarget(null)}
        />
      )}

      {documentsModal && order && (
        <B2BDocumentsModal
          order={order}
          onClose={() => setDocumentsModal(false)}
          onSaved={refresh}
        />
      )}

      {shipmentModal && order && (
        <B2BShipmentModal
          order={order}
          shipment={shipmentModal.shipment}
          onClose={() => setShipmentModal(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
